import React, { FC, memo, ReactElement, useCallback } from "react";
import { useSelector } from "react-redux";
import InfiniteScroll from "react-infinite-scroll-component";
import { Button, Typography } from "@material-ui/core";

import TweetComponent from "../../components/TweetComponent/TweetComponent";
import { useUserPageStyles } from "./UserPageStyles";
import AddTweetModal from "../../components/AddTweetModal/AddTweetModal";
import Spinner from "../../components/Spinner/Spinner";
import {
    selectIsUserTweetsLoading,
    selectPagesCount,
    selectUserTweetsItems
} from "../../store/ducks/userTweets/selectors";
import { selectUserDataId } from "../../store/ducks/user/selectors";
import { selectUserProfileId, selectUserProfileIsUserBlocked, selectUserProfileUsername } from "../../store/ducks/userProfile/selectors";
import { useModalWindow } from "../../hook/useModalWindow";

interface UserPageTweetsProps {
    userTweetsActiveTab: number;
    page: number;
    loadUserTweets: () => void;
}

const UserPageTweets: FC<UserPageTweetsProps> = memo(({ userTweetsActiveTab, page, loadUserTweets }): ReactElement => {
    const classes = useUserPageStyles();
    const myProfileId = useSelector(selectUserDataId);
    const userProfileId = useSelector(selectUserProfileId);
    const isUserBlocked = useSelector(selectUserProfileIsUserBlocked);
    const username = useSelector(selectUserProfileUsername);
    const tweets = useSelector(selectUserTweetsItems);
    const isTweetsLoading = useSelector(selectIsUserTweetsLoading);
    const pagesCount = useSelector(selectPagesCount);
    const { visibleModalWindow, onOpenModalWindow, onCloseModalWindow } = useModalWindow();

    const getText = useCallback((): string => {
      if (userProfileId === myProfileId) {
        return "You haven’t any Tweets yet";
      } else if (isUserBlocked) {
        return `@${username} is blocked`;
      } else {
        return `@${username} hasn’t any Tweets`
      }
    }, [userProfileId, myProfileId, isUserBlocked, username]);

    const getreplyText = useCallback((): string => {
      if (userProfileId === myProfileId) {
        return "You haven’t any replies yet";
      } else if (isUserBlocked) {
        return `@${username} is blocked`;
      } else {
        return `@${username} hasn’t any replies`
      }
    }, [userProfileId, myProfileId, isUserBlocked, username]);


    const getPhotoVideo = useCallback((): string => {
      if (userProfileId === myProfileId) {
        return "You haven’t Tweeted any photos or videos yet";
      } else if (isUserBlocked) {
        return `@${username} is blocked`;
      } else {
        return `@${username} hasn’t Tweeted any photos or videos`;
      }
    }, [userProfileId, myProfileId, isUserBlocked, username]);

    const getlike = useCallback((): string => {
      if (userProfileId === myProfileId) {
        return "You don’t have any likes yet";
      } else if (isUserBlocked) {
        return `@${username} is blocked`;
      } else {
        return `@${username} hasn’t liked any Tweets`;
      }
    }, [userProfileId, myProfileId, isUserBlocked, username]);

    const renderTweets = () => {
        if (tweets?.length === 0 && userTweetsActiveTab === 0 && !isTweetsLoading) {
            return (
                <div className={classes.textWrapper}>
                    <Typography variant={"h5"}>
                        {getText()}
                    </Typography>
                    <Typography variant={"subtitle1"}>
                        {userProfileId === myProfileId ? (
                            "When you send Tweets, they will show up here."
                        ) : (
                            "When they do, their Tweets show up here."
                        )}
                    </Typography>
                    {(userProfileId === myProfileId) && (
                        <Button
                            className={classes.button}
                            onClick={onOpenModalWindow}
                            variant="contained"
                            color="primary"
                            size="medium"
                        >
                            Send Tweet
                        </Button>
                    )}
                </div>
            );
        } else if (tweets?.length === 0 && userTweetsActiveTab === 1 && !isTweetsLoading) {
            return (
                <div className={classes.textWrapper}>
                    <Typography variant={"h5"}>
                        {getreplyText()}
                    </Typography>
                    <Typography variant={"subtitle1"}>
                        {(userProfileId === myProfileId) ? (
                            "When you reply Tweets, they will show up here."
                        ) : (
                            "When they do, their replies show up here."
                        )}
                    </Typography>
                </div>
            );
        } else if (tweets?.length === 0 && userTweetsActiveTab === 2 && !isTweetsLoading) {
            return (
                <div className={classes.textWrapper}>
                    <Typography variant={"h5"}>
                        {getPhotoVideo()}
                    </Typography>
                    <Typography variant={"subtitle1"}>
                        {(userProfileId === myProfileId) ? (
                            "When you send Tweets with photos or videos in them, it will show up here."
                        ) : (
                            "When they do, their media will show up here."
                        )}
                    </Typography>
                    {(userProfileId === myProfileId) && (
                        <Button
                            className={classes.button}
                            onClick={onOpenModalWindow}
                            variant="contained"
                            color="primary"
                            size="medium"
                        >
                            Tweet a photo or video
                        </Button>
                    )}
                </div>
            );
        } else if (tweets?.length === 0 && userTweetsActiveTab === 3 && !isTweetsLoading) {
            return (
                <div className={classes.textWrapper}>
                    <Typography variant={"h5"}>
                        {getlike()}
                    </Typography>
                    <Typography variant={"subtitle1"}>
                        {(userProfileId === myProfileId) ? (
                            "Tap the heart on any Tweet to show it some love. When you do, it’ll show up here."
                        ) : (
                            "When they do, those Tweets will show up here."
                        )}
                    </Typography>
                </div>
            );
        } else {
            return (
                <>
                    {tweets?.map((tweet) => (
                        <TweetComponent key={tweet.id} tweet={tweet} activeTab={userTweetsActiveTab} />
                    ))}
                    {isTweetsLoading && <Spinner />}
                </>
            );
        }
    };

    return (
        <InfiniteScroll
            style={{ overflow: "unset" }}
            dataLength={tweets.length}
            next={loadUserTweets}
            hasMore={page < pagesCount}
            loader={null}
        >
            {renderTweets()}
            <AddTweetModal visible={visibleModalWindow} onClose={onCloseModalWindow} />
        </InfiniteScroll>
    );
});

export default UserPageTweets;
